import { useCallback, useEffect, useRef, useState } from 'react';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import config from '../config.js';

/** How long the reveal stays up after a moment ends. */
export const REVEAL_MS = 8000;

/**
 * The live moment on a band's show (ENG-690/691): a trivia question everyone
 * watching can answer for a few seconds, then see revealed.
 *
 * Moments come and go on realtime frames (`frame(data)` takes
 * live_moment_start, _count and _end from the stream-status socket), never
 * on a client timer alone; a page that opens mid-moment reads
 * `GET /public/bands/{id}/moments/current`. `answer(option)` posts the
 * listener's one answer. The reveal compares the end frame's correctOption
 * with the option chosen for the same momentId, so a stale moment is never
 * judged against a newer one.
 *
 * State: `moment` (the question, or null), `chosen` (the option sent),
 * `result` ({ correctOption, answered, percentRight } once ended), `answered`
 * (the live count), `error` (a stable code) and `offsetMs` (server clock
 * minus ours, for the countdown).
 */
export const useLiveMoment = (bandId, { enabled = true } = {}) => {
  const fetchWrapperRef = useRef(useFetchWrapper());
  const [moment, setMoment] = useState(null);
  const [chosen, setChosen] = useState(null);
  const [result, setResult] = useState(null);
  const [answered, setAnswered] = useState(0);
  const [error, setError] = useState(null);
  const [dismissed, setDismissed] = useState(null);
  // An after-song rating's votes (ENG-693): { song?: bool, artist?: bool }.
  const [votes, setVotes] = useState({});
  const [offsetMs, setOffsetMs] = useState(0);
  const momentIdRef = useRef(null);
  const revealTimer = useRef(null);

  const open = useCallback((m) => {
    clearTimeout(revealTimer.current);
    momentIdRef.current = m.momentId;
    setMoment(m);
    setChosen(null);
    setVotes({});
    setResult(null);
    setError(null);
    setAnswered(m.answered ?? 0);
    const server = Date.parse(m.serverNow);
    setOffsetMs(Number.isFinite(server) ? server - Date.now() : 0);
  }, []);

  const close = useCallback(() => {
    clearTimeout(revealTimer.current);
    momentIdRef.current = null;
    setMoment(null);
    setResult(null);
  }, []);

  useEffect(() => {
    if (!enabled || !bandId) {
      close();
      return undefined;
    }
    let cancelled = false;
    fetchWrapperRef.current
      .get(`${config.apiBaseUrl}/public/bands/${bandId}/moments/current`)
      .then((answer) => {
        if (!cancelled && answer?.moment && momentIdRef.current == null) open(answer.moment);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [bandId, enabled, open, close]);

  useEffect(() => () => clearTimeout(revealTimer.current), []);

  const frame = useCallback((data) => {
    if (!data) return;
    if (data.type === 'live_moment_start') {
      open(data);
      return;
    }
    if (data.momentId !== momentIdRef.current) return;
    if (data.type === 'live_moment_count') {
      setAnswered(data.answered);
    } else if (data.type === 'live_moment_end') {
      setAnswered(data.answered);
      setResult({ kind: data.kind, correctOption: data.correctOption, answered: data.answered, percentRight: data.percentRight });
      revealTimer.current = setTimeout(close, REVEAL_MS);
    }
  }, [open, close]);

  const answer = useCallback(async (option) => {
    const id = momentIdRef.current;
    if (id == null || chosen != null || result) return;
    setChosen(option);
    setError(null);
    try {
      await fetchWrapperRef.current.post(`${config.apiBaseUrl}/bands/${bandId}/moments/${id}/answers`, { option });
    } catch (err) {
      // Already answered elsewhere: the choice stands; anything else frees it.
      if (err?.code !== 'moment_already_answered') setChosen(null);
      setError(err?.code || 'moment_answer_failed');
    }
  }, [bandId, chosen, result]);

  // Votes on an after-song rating: each question on its own, a later answer
  // replacing the earlier, while the rating is open.
  const rate = useCallback(async (question, liked) => {
    const id = momentIdRef.current;
    if (id == null || result) return;
    const before = votes[question];
    setVotes((prev) => ({ ...prev, [question]: liked }));
    setError(null);
    try {
      await fetchWrapperRef.current.post(`${config.apiBaseUrl}/bands/${bandId}/moments/${id}/ratings`, { question, liked });
    } catch (err) {
      setVotes((prev) => ({ ...prev, [question]: before }));
      setError(err?.code || 'moment_answer_failed');
    }
  }, [bandId, result, votes]);

  const dismiss = useCallback(() => setDismissed(momentIdRef.current), []);

  const visible = moment && dismissed !== moment.momentId ? moment : null;
  return { moment: visible, chosen, votes, result, answered, error, offsetMs, frame, answer, rate, dismiss };
};
